import type { TrackingConsent } from "@/utils/tracking/consent";

// Direct Google tags (Google Ads, GA4) run in Consent Mode "basic": nothing is
// loaded until the visitor accepts in our cookie banner. The dataLayer gets
// the "denied" default first (GoogleConsentDefault, or ensureGoogleConsentDefault
// when our code gets there first), then an update with each choice.

export const GOOGLE_ADS_TAG_ID = "AW-11363808812";
// "G.G Event Purchase" conversion action.
export const GOOGLE_ADS_PURCHASE_SEND_TO = "AW-11363808812/yJk8CPzB-cAZEKzs16oq";
export const GOOGLE_ANALYTICS_TAG_ID = "G-TXN1NZSGCL";

const GTAG_SCRIPT_ID = "guestgallery-gtag-js";

type Gtag = (...args: unknown[]) => void;

interface GtagWindow {
  dataLayer?: unknown[];
  gtag?: (...args: any[]) => void;
  __guestGalleryGtagConfigs?: Record<string, true>;
  /** Set once the Consent Mode default is in the dataLayer. */
  __ggConsentDefault?: true;
}

function getGtag(): Gtag {
  const w = window as unknown as GtagWindow;
  const dataLayer = (w.dataLayer = w.dataLayer || []);
  if (typeof w.gtag !== "function") {
    // gtag.js only accepts the Arguments object, not an array.
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      dataLayer.push(arguments);
    };
  }
  return w.gtag;
}

// Same values as GoogleConsentDefault's inline script. All our traffic is
// EEA, so the default is "denied" everywhere.
const DENIED = "denied";
const GRANTED = "granted";

/** Pushes the Consent Mode default once per document, before any update. */
export function ensureGoogleConsentDefault(): Gtag {
  const gtag = getGtag();
  const w = window as unknown as GtagWindow;
  if (!w.__ggConsentDefault) {
    gtag("consent", "default", {
      ad_storage: DENIED,
      ad_user_data: DENIED,
      ad_personalization: DENIED,
      analytics_storage: DENIED,
    });
    gtag("set", "ads_data_redaction", true);
    w.__ggConsentDefault = true;
  }
  return gtag;
}

/** Tells Google tags the visitor's choice (Consent Mode v2, basic). */
export function updateGoogleConsent(consent: TrackingConsent): void {
  const ads = consent.advertisement ? GRANTED : DENIED;
  ensureGoogleConsentDefault()("consent", "update", {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: consent.analytics ? GRANTED : DENIED,
  });
}

/** Loads gtag.js once and configures `tagId`. Call only with consent. */
export function ensureGoogleTag(
  tagId: string,
  config?: Record<string, unknown>,
): Gtag {
  const gtag = ensureGoogleConsentDefault();
  const w = window as unknown as GtagWindow;
  const configs = (w.__guestGalleryGtagConfigs = w.__guestGalleryGtagConfigs || {});
  const configKey = `${tagId}:${JSON.stringify(config ?? {})}`;
  if (configs[configKey]) return gtag;

  if (!document.getElementById(GTAG_SCRIPT_ID)) {
    gtag("js", new Date());
    const script = document.createElement("script");
    script.id = GTAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`;
    document.head.appendChild(script);
  }
  gtag("config", tagId, config ?? {});
  configs[configKey] = true;
  return gtag;
}

/** Enhanced conversions: the email is passed as data, never as script text. */
export function setGoogleUserDataEmail(gtag: Gtag, email?: string | null): void {
  const normalizedEmail = email?.trim().toLowerCase();
  if (normalizedEmail) {
    gtag("set", "user_data", { email: normalizedEmail });
  }
}

export function trackGoogleAdsPurchase({
  value,
  currency,
  transactionId,
  email,
}: {
  value?: number;
  currency?: string;
  transactionId: string;
  email?: string | null;
}): void {
  const gtag = ensureGoogleTag(GOOGLE_ADS_TAG_ID);
  setGoogleUserDataEmail(gtag, email);
  gtag("event", "conversion", {
    send_to: GOOGLE_ADS_PURCHASE_SEND_TO,
    ...(typeof value === "number" ? { value } : {}),
    ...(currency ? { currency } : {}),
    transaction_id: transactionId,
  });
}
